/**
 * SubirComprobante.tsx — El técnico sube la foto de un comprobante bancario y
 * n8n devuelve el texto que leyó Google Vision y el enlace del archivo en Drive.
 * Ruta /menu/comprobantes, solo administradores.
 *
 *   POST multipart a REACT_APP_N8N_URL_COMPROBANTE_LEER
 *     file  → la imagen
 *     datos → JSON { idtecnico, tecnico, app_version }
 *
 * Respuesta real de n8n (28-sep-2026): [{ webContentLink, webViewLink, thumbnailLink,
 * responses:[{ textAnnotations:[{ description:'texto…' }] }] }]. Se tolera también que
 * el texto venga como `texto`/`text`/`raw` o en `fullTextAnnotation.text`, y el enlace
 * como `drive.webViewLink`, `webViewLink` o `link`. Error: { estado:'error', mensaje }.
 *
 * Si Vision no reconoce texto, se muestra igual su respuesta cruda (JSON) para no
 * perder el comprobante. Los leídos en la sesión quedan en una lista abajo, con su
 * enlace, porque se suben muchos seguidos. Solo muestra lo que devuelve n8n: no
 * registra pagos ni toca MikroWisp.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Share } from '@capacitor/share';
import { FaArrowLeft } from 'react-icons/fa';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import './SubirComprobante.css';

const APP_VERSION: string = (require('../../package.json') as { version: string }).version;

const URL_LEER = process.env.REACT_APP_N8N_URL_COMPROBANTE_LEER;
const TIEMPO_MAXIMO_MS = 60000; // Vision + Drive tardan varios segundos

const BackIcon = FaArrowLeft as unknown as React.FC;

type Resultado = {
  texto: string;          // texto reconocido; vacío si Vision no leyó nada
  crudo: string;          // respuesta de Vision en JSON, para cuando no hay texto
  enlace: string;
  nombreArchivo: string;
};

type Leido = { hora: string; resumen: string; enlace: string };

const CLAVE_HISTORIAL = 'comprobantes_leidos';
const MAX_HISTORIAL = 30;

const leerHistorial = (): Leido[] => {
  try { return JSON.parse(sessionStorage.getItem(CLAVE_HISTORIAL) || '[]'); } catch { return []; }
};

const dataUrlAArchivo = (dataUrl: string, nombre: string): File => {
  const [cabecera, datos] = dataUrl.split(',');
  const tipo = cabecera.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binario = atob(datos);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return new File([bytes], nombre, { type: tipo });
};

/** Saca el texto y el enlace de Drive de la respuesta, venga como venga. Nunca pierde la respuesta cruda. */
const interpretar = (r: any): Resultado | null => {
  if (!r || typeof r !== 'object') return null;
  // El primer candidato que sea un texto de verdad (un objeto daría "[object Object]")
  const esTexto = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
  const texto = [
    r.texto, r.text, r.textoOcr, r.vision?.text,
    r.responses?.[0]?.fullTextAnnotation?.text,
    r.vision?.responses?.[0]?.fullTextAnnotation?.text,
    r.responses?.[0]?.textAnnotations?.[0]?.description,
    r.raw,
  ].find(esTexto) ?? '';
  const drive = (r.drive && typeof r.drive === 'object') ? r.drive : {};
  const enlace = [drive.webViewLink, drive.link, r.webViewLink, r.link, r.drive_link].find(esTexto) ?? '';
  const nombreArchivo = [drive.nombre, drive.name, r.nombre].find(esTexto) ?? '';
  // Lo que devolvió Vision, tal cual, por si no reconoció texto (o para revisar qué vio)
  const vision = r.responses ?? r.vision ?? r.raw ?? r;
  const crudo = typeof vision === 'string' ? vision : JSON.stringify(vision, null, 2);
  return { texto: texto.trim(), crudo, enlace, nombreArchivo };
};

/** Primeras líneas con contenido, para la lista de la sesión. */
const resumir = (texto: string): string => {
  const lineas = texto.split('\n').map(l => l.trim()).filter(l => l.length > 3);
  const util = lineas.filter(l => /\$|\d{2,}|comprobante|transferencia|dep[oó]sito/i.test(l));
  return (util.length ? util : lineas).slice(0, 3).join(' · ').slice(0, 120) || 'Sin texto reconocido';
};

const SubirComprobante: React.FC = () => {
  const navigate = useNavigate();
  const clientData = (() => { try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; } })();

  const [foto, setFoto] = useState<{ dataUrl: string; archivo: File } | null>(null);
  const [abriendo, setAbriendo] = useState(false);
  const [errorFoto, setErrorFoto] = useState('');

  const [leyendo, setLeyendo] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [copiado, setCopiado] = useState<'texto' | 'enlace' | 'error' | null>(null);
  const [historial, setHistorial] = useState<Leido[]>(leerHistorial);
  const resultadoRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!clientData?.token_cliente) navigate('/menu');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Segundos que lleva la lectura, para que se note que algo pasa
  useEffect(() => {
    if (!leyendo) { setSegundos(0); return; }
    const id = setInterval(() => setSegundos(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [leyendo]);

  useEffect(() => {
    if (resultado) resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [resultado]);

  const tomarFoto = async (origen: CameraSource) => {
    setErrorFoto('');
    setAbriendo(true);
    try {
      // 1600 px: los comprobantes tienen letra pequeña y el OCR la necesita nítida
      const f = await Camera.getPhoto({
        quality: 80, width: 1600, resultType: CameraResultType.DataUrl, source: origen, correctOrientation: true,
      });
      if (!f.dataUrl) return;
      setFoto({ dataUrl: f.dataUrl, archivo: dataUrlAArchivo(f.dataUrl, 'comprobante.jpg') });
      setResultado(null);
      setError('');
    } catch (e: any) {
      const m = String(e?.message || e);
      // Cerrar la cámara o la galería sin elegir nada no es un error
      if (/cancel|no image picked/i.test(m)) return;
      const esCamara = origen === CameraSource.Camera;
      setErrorFoto(/denied|permission|permiso/i.test(m)
        ? `Permite el acceso a ${esCamara ? 'la cámara' : 'fotos y videos'} en Ajustes › Aplicaciones › SisnetelApp › Permisos.`
        : `No se pudo abrir ${esCamara ? 'la cámara' : 'la galería'}.`);
    } finally {
      setAbriendo(false);
    }
  };

  const leer = async () => {
    if (!foto) return;
    if (!URL_LEER) { setError('Falta configurar el webhook de n8n (REACT_APP_N8N_URL_COMPROBANTE_LEER).'); return; }

    const cuerpo = new FormData();
    cuerpo.append('file', foto.archivo, foto.archivo.name);
    cuerpo.append('datos', JSON.stringify({
      idtecnico: clientData.id_tecnico,
      tecnico: clientData.nombre,
      app_version: APP_VERSION,
    }));

    const controlador = new AbortController();
    const temporizador = setTimeout(() => controlador.abort(), TIEMPO_MAXIMO_MS);
    setLeyendo(true);
    setError('');
    setResultado(null);
    try {
      const res = await fetch(URL_LEER, { method: 'POST', body: cuerpo, signal: controlador.signal });
      // Se lee como texto y luego se intenta el JSON: si no lo es, igual se muestra lo que llegó
      const cuerpoTexto = await res.text();
      let d: any = null;
      try { d = JSON.parse(cuerpoTexto); } catch { d = null; }
      const r = Array.isArray(d) ? d[0] : d;
      if (!res.ok) throw new Error(r?.mensaje || r?.message || `El servidor respondió ${res.status}: ${cuerpoTexto.slice(0, 200)}`);
      if (r?.estado === 'error') throw new Error(r.mensaje || 'n8n devolvió un error.');
      const interpretado: Resultado = (r && interpretar(r)) || {
        // No es la respuesta de un comprobante leído: se entrega tal cual, sin perderla
        texto: '', crudo: cuerpoTexto.trim() || `Respuesta vacía (HTTP ${res.status})`, enlace: '', nombreArchivo: '',
      };
      setResultado(interpretado);
      const dos = (n: number) => String(n).padStart(2, '0');
      const ahora = new Date();
      const nuevo: Leido = { hora: `${dos(ahora.getHours())}:${dos(ahora.getMinutes())}`, resumen: resumir(interpretado.texto), enlace: interpretado.enlace };
      const lista = [nuevo, ...historial].slice(0, MAX_HISTORIAL);
      setHistorial(lista);
      try { sessionStorage.setItem(CLAVE_HISTORIAL, JSON.stringify(lista)); } catch { /* sin espacio */ }
    } catch (e: any) {
      if (e?.name === 'AbortError') setError('El servidor tardó demasiado. Intenta de nuevo.');
      else if (e instanceof TypeError) setError('No se pudo conectar. Revisa tu conexión.');
      else setError(e?.message || 'No se pudo leer el comprobante.');
    } finally {
      clearTimeout(temporizador);
      setLeyendo(false);
    }
  };

  const copiar = async (que: 'texto' | 'enlace') => {
    const valor = que === 'texto' ? (resultado?.texto || resultado?.crudo) : resultado?.enlace;
    if (!valor) return;
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(que);
    } catch {
      // WebView sin portapapeles o sin foco: que el técnico sepa que no copió
      setCopiado('error');
    }
    setTimeout(() => setCopiado(null), 2500);
  };

  const compartir = async () => {
    if (!resultado) return;
    const texto = [resultado.texto || resultado.crudo, resultado.enlace ? `Drive: ${resultado.enlace}` : ''].filter(Boolean).join('\n\n');
    // Hoja de compartir del teléfono (WhatsApp, correo, Telegram…); en la web, lo que tenga el navegador
    try {
      await Share.share({ title: 'Comprobante', text: texto, dialogTitle: 'Compartir comprobante' });
    } catch (e: any) {
      if (/cancel|abort/i.test(String(e?.message || e))) return;
      window.open(`https://wa.me/?text=${encodeURIComponent(texto.slice(0, 1500))}`, '_blank');
    }
  };

  const otro = () => {
    setFoto(null);
    setResultado(null);
    setError('');
    // El scroll vive en el contenedor interno, no en window
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto' as const }}>
        <div className="sc-cabecera">
          <button type="button" onClick={() => navigate(-1)} aria-label="Volver" className="sc-volver"><BackIcon /></button>
          <div className="sc-titulo">Subir comprobante</div>
        </div>

        <div className="sc-cuerpo">
          <div className="sc-tarjeta">
            <div className="sc-seccion">Foto del comprobante</div>
            {foto && <img src={foto.dataUrl} alt="Comprobante" className="sc-preview" />}
            <div className="sc-botones-foto">
              <button type="button" className="sc-boton-foto" onClick={() => tomarFoto(CameraSource.Camera)} disabled={abriendo || leyendo}>
                📷 {foto ? 'Otra foto' : 'Tomar foto'}
              </button>
              <button type="button" className="sc-boton-foto" onClick={() => tomarFoto(CameraSource.Photos)} disabled={abriendo || leyendo}>
                🖼️ Galería
              </button>
            </div>
            {errorFoto && <div className="sc-error">{errorFoto}</div>}
            <div className="sc-ayuda">Que se lean bien el monto, la fecha y el número del comprobante.</div>
          </div>

          <button type="button" className="sc-principal" onClick={leer} disabled={!foto || leyendo}>
            {leyendo
              ? <><Spinner animation="border" size="sm" /> Leyendo con Google Vision… {segundos}s</>
              : '🔍 Leer comprobante'}
          </button>
          {!foto && <div className="sc-ayuda sc-centrado">Toma la foto del comprobante para poder leerlo.</div>}
          {error && (
            <div className="sc-error sc-error--bloque" role="alert">
              {error}
              <button type="button" className="sc-enlace" onClick={leer}>Reintentar</button>
            </div>
          )}

          {resultado && (
            <div ref={resultadoRef} className="sc-tarjeta sc-resultado">
              {resultado.texto ? (
                <>
                  <div className="sc-seccion">Texto leído por Google Vision</div>
                  <pre className="sc-texto">{resultado.texto}</pre>
                </>
              ) : (
                <>
                  <div className="sc-seccion">Google Vision no reconoció texto</div>
                  <div className="sc-cuenta sc-cuenta--duda">⚠️ Esta es su respuesta tal cual. Prueba con una foto más nítida o con más luz.</div>
                  <pre className="sc-texto sc-texto--crudo">{resultado.crudo}</pre>
                </>
              )}
              <button type="button" className="sc-boton sc-boton--ancho" onClick={() => copiar('texto')}>
                {copiado === 'texto' ? '✅ Copiado' : copiado === 'error' ? '⚠️ No se pudo copiar' : resultado.texto ? 'Copiar texto' : 'Copiar respuesta'}
              </button>

              <div className="sc-seccion" style={{ marginTop: 14 }}>Drive</div>
              {resultado.enlace ? (
                <>
                  {resultado.nombreArchivo && <div className="sc-drive-nombre">📁 {resultado.nombreArchivo}</div>}
                  <div className="sc-acciones">
                    <a className="sc-boton" href={resultado.enlace} target="_blank" rel="noreferrer">Abrir</a>
                    <button type="button" className="sc-boton" onClick={() => copiar('enlace')}>{copiado === 'enlace' ? '✅ Copiado' : copiado === 'error' ? '⚠️ No copió' : 'Copiar enlace'}</button>
                    <button type="button" className="sc-boton" onClick={compartir}>Compartir</button>
                  </div>
                </>
              ) : (
                <div className="sc-ayuda">n8n no devolvió el enlace de Drive.</div>
              )}

              <button type="button" className="sc-boton sc-boton--ancho" onClick={otro}>Subir otro comprobante</button>
            </div>
          )}

          {historial.length > 0 && (
            <div className="sc-tarjeta">
              <div className="sc-seccion">Leídos en esta sesión ({historial.length})</div>
              <div className="sc-historial">
                {historial.map((h, i) => (
                  <div key={i} className="sc-historial-fila">
                    <div className="sc-historial-texto">
                      <span className="sc-historial-hora">{h.hora}</span> {h.resumen}
                    </div>
                    {h.enlace && <a className="sc-enlace" href={h.enlace} target="_blank" rel="noreferrer">Drive</a>}
                  </div>
                ))}
              </div>
              <div className="sc-ayuda">Se borra al cerrar la app. Los archivos siguen en Drive.</div>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SubirComprobante;
